import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Download,
    Trash2,
    Mail,
    Phone,
    Calendar,
    ExternalLink,
    X,
    Eye,
    Search,
    Filter,
    ChevronDown,
    MapPin,
    Briefcase,
    Star,
    SlidersHorizontal,
    RotateCcw
} from 'lucide-react';
import { Button } from '../../ui/button';
import { toast } from 'sonner';
import { Badge } from '../../ui/badge';
import { AdminLayout } from './components/AdminLayout';
import { AdminTopBar } from './components/AdminTopBar';
import { BackLink } from './components/BackLink';
import { EmptyState } from './components/EmptyState';
import { Pagination } from './components/Pagination';
import { applicationStorage, type StoredApplication } from '../../utils/applicationStorage';
import { downloadBase64File } from '../../utils/resumeStorage';
import { DeleteConfirmationModal } from './components/DeleteConfirmationModal';
import { AdvancedFilterModal, type AdvancedFilters } from './components/AdvancedFilterModal';
import { FilterDebugPanel } from './components/FilterDebugPanel';
import { applicationsAPI } from '../../../services/api';
import { exportApplicationsToExcel } from '../../utils/excelExport';
import { useSocketEvent } from '../../contexts/SocketContext';

export function AdminApplications() {
    const [applications, setApplications] = useState<StoredApplication[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [jobFilter, setJobFilter] = useState<string>('all');
    const [selectedApplication, setSelectedApplication] = useState<StoredApplication | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const [deleteModal, setDeleteModal] = useState({
        isOpen: false,
        itemId: '',
        itemName: '',
        isDeleting: false
    });
    const [isLoading, setIsLoading] = useState(true);
    const [isAdvancedFilterOpen, setIsAdvancedFilterOpen] = useState(false);
    const [advancedFilters, setAdvancedFilters] = useState<AdvancedFilters>({
        qualifications: [],
        yearOfPassingFrom: '',
        yearOfPassingTo: '',
        experienceLevel: [],
        experienceYearsFrom: '',
        experienceYearsTo: '',
        noticePeriods: [],
        skills: []
    });

    // Get current user from localStorage to check if super_admin
    const currentUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
    const isSuperAdmin = currentUser?.role === 'super_admin';

    // Fetch applications from MongoDB API
    const fetchApplications = async () => {
        try {
            setIsLoading(true);
            const response = await applicationsAPI.getAll();
            
            // Map MongoDB applications to match StoredApplication interface
            const apiApplications = response.data.map((app: any) => ({
                id: app._id || app.id,
                name: app.name,
                email: app.email,
                phone: app.phone,
                location: app.location || '',
                position: app.position,
                jobId: app.careerId || app.jobId || '',
                experience: app.experience,
                experienceLevel: app.experienceLevel || '',
                education: app.education || '',
                qualification: app.qualification || '',
                specialization: app.specialization || '',
                university: app.university || '',
                yearOfPassing: app.yearOfPassing || '',
                percentageCgpa: app.percentageCgpa || '',
                totalExperience: app.experience || '',
                previousJobTitle: app.previousJobTitle || '',
                currentCtc: app.currentCtc || '',
                expectedSalary: app.expectedSalary || '',
                noticePeriod: app.noticePeriod || '',
                preferredLocation: app.preferredLocation || '',
                willingToRelocate: app.willingToRelocate || '',
                employmentType: app.employmentType || '',
                coverLetter: app.coverLetter || '',
                currentAddress: app.currentAddress || '',
                linkedinUrl: app.linkedinUrl || '',
                portfolioUrl: app.portfolioUrl || '',
                dob: app.dob || '',
                gender: app.gender || '',
                resumeUrl: app.resumeUrl || '',
                resumeFileName: app.resumeFileName || '',
                status: app.status || 'new',
                visibility: app.visibility || 'visible',
                appliedDate: app.createdAt || app.appliedDate || new Date().toISOString(),
                rating: app.rating || 0,
                skills: app.skills || []
            }));

            setApplications(apiApplications);
        } catch (error) {
            console.error('Error fetching applications from API:', error);
            toast.error('Failed to load applications from database');
            setApplications([]);
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch on mount
    useEffect(() => {
        fetchApplications();
    }, []);

    // Real-time socket event handlers
    useSocketEvent('application:created', useCallback((data: any) => {
        if (!data?.application) return; // Guard against undefined data
        const app = data.application;
        const newApp: StoredApplication = {
            id: app._id || app.id,
            name: app.name,
            email: app.email,
            phone: app.phone,
            jobTitle: app.jobTitle || '',
            jobId: app.jobId || '',
            experienceLevel: app.experienceLevel || '',
            education: app.education || '',
            qualification: app.qualification || '',
            specialization: app.specialization || '',
            university: app.university || '',
            yearOfPassing: app.yearOfPassing || '',
            percentageCgpa: app.percentageCgpa || '',
            totalExperience: app.experience || '',
            previousJobTitle: app.previousJobTitle || '',
            currentCtc: app.currentCtc || '',
            expectedSalary: app.expectedSalary || '',
            noticePeriod: app.noticePeriod || '',
            preferredLocation: app.preferredLocation || '',
            willingToRelocate: app.willingToRelocate || '',
            employmentType: app.employmentType || '',
            coverLetter: app.coverLetter || '',
            currentAddress: app.currentAddress || '',
            linkedinUrl: app.linkedinUrl || '',
            portfolioUrl: app.portfolioUrl || '',
            dob: app.dob || '',
            gender: app.gender || '',
            resumeUrl: app.resumeUrl || '',
            resumeFileName: app.resumeFileName || '',
            status: app.status || 'new',
            visibility: app.visibility || 'visible',
            appliedDate: app.createdAt || app.appliedDate || new Date().toISOString(),
            rating: app.rating || 0,
            skills: app.skills || []
        };
        setApplications(prev => [newApp, ...prev]);
        toast.info(`New application received from ${app.name}!`);
    }, []));

    useSocketEvent('application:updated', useCallback((data: any) => {
        if (!data?.application) return; // Guard against undefined data
        const app = data.application;
        setApplications(prev => prev.map(a => {
            if (a.id === (app._id || app.id)) {
                return {
                    ...a,
                    status: app.status ?? a.status,
                    visibility: app.visibility ?? a.visibility,
                    rating: app.rating ?? a.rating,
                };
            }
            return a;
        }));
        toast.info('Application updated');
    }, []));

    useSocketEvent('application:deleted', useCallback((data: any) => {
        if (!data) return; // Guard against undefined data
        const deletedId = data.application?._id || data.application?.id || data.id;
        if (!deletedId) return;
        setApplications(prev => prev.filter(a => a.id !== deletedId));
        toast.info('Application removed');
    }, []));

    // Refresh applications from API
    const refreshApplications = () => {
        fetchApplications();
    };

    const handleStatusChange = async (id: string, newStatus: StoredApplication['status']) => {
        try {
            // Update in MongoDB via API
            await applicationsAPI.update(id, { status: newStatus });
            
            // Update local state
            const updatedApplications = applications.map(app => 
                app.id === id ? { ...app, status: newStatus } : app
            );
            setApplications(updatedApplications);
            
            if (selectedApplication?.id === id) {
                setSelectedApplication({ ...selectedApplication, status: newStatus });
            }
            
            toast.success('Status updated');
        } catch (error) {
            console.error('Error updating status:', error);
            toast.error('Failed to update status');
        }
    };

    const handleDelete = (id: string) => {
        setDeleteModal({
            isOpen: true,
            itemId: id,
            itemName: selectedApplication?.name || '',
            isDeleting: false
        });
    };

    const confirmDelete = async () => {
        setDeleteModal(prev => ({ ...prev, isDeleting: true }));
        try {
            await applicationsAPI.delete(deleteModal.itemId);
            if (isSuperAdmin) {
                // Super admin: item is permanently deleted, remove from list
                const updatedApplications = applications.filter(a => a.id !== deleteModal.itemId);
                setApplications(updatedApplications);
            } else {
                // Regular admin: item is soft-deleted, update visibility
                const updatedApplications = applications.map(a =>
                    a.id === deleteModal.itemId ? { ...a, visibility: 'hidden' as const } : a
                );
                setApplications(updatedApplications);
            }
            if (selectedApplication?.id === deleteModal.itemId) {
                setSelectedApplication(null);
            }
            toast.success(isSuperAdmin ? 'Application permanently deleted' : 'Application deleted successfully');
            setDeleteModal({
                isOpen: false,
                itemId: '',
                itemName: '',
                isDeleting: false
            });
        } catch (error) {
            toast.error('Failed to delete application');
            setDeleteModal(prev => ({ ...prev, isDeleting: false }));
        }
    };

    const handleApplyAdvancedFilters = (filters: AdvancedFilters) => {
        setAdvancedFilters(filters);
        setCurrentPage(1); // Reset to first page when filters change
    };

    const handleExport = () => {
        try {
            // Get the currently filtered applications
            const dataToExport = filteredApplications;
            
            if (dataToExport.length === 0) {
                toast.error('No data to export');
                return;
            }

            // Generate filename with timestamp
            const timestamp = new Date().toISOString().split('T')[0];
            const filterInfo = getActiveFilterCount() > 0 ? '_filtered' : '_all';
            const filename = `applications${filterInfo}_${timestamp}.xlsx`;

            // Export to Excel
            exportApplicationsToExcel(dataToExport, filename);
            
            toast.success(`Exported ${dataToExport.length} application(s) successfully`);
        } catch (error) {
            console.error('Export error:', error);
            toast.error('Failed to export data');
        }
    };

    const getActiveFilterCount = (): number => {
        let count = 0;
        if (jobFilter !== 'all') count++;
        if (advancedFilters.qualifications.length > 0) count++;
        if (advancedFilters.yearOfPassingFrom || advancedFilters.yearOfPassingTo) count++;
        if (advancedFilters.experienceLevel.length > 0) count++;
        if (advancedFilters.experienceYearsFrom || advancedFilters.experienceYearsTo) count++;
        if (advancedFilters.noticePeriods.length > 0) count++;
        if (advancedFilters.skills.length > 0) count++;
        return count;
    };

    // Get unique job positions with applicant count
    const getAvailableJobs = (): { position: string; count: number }[] => {
        const jobsMap = new Map<string, number>();
        applications.forEach(app => {
            if (app.position) {
                jobsMap.set(app.position, (jobsMap.get(app.position) || 0) + 1);
            }
        });
        return Array.from(jobsMap.entries())
            .map(([position, count]) => ({ position, count }))
            .sort((a, b) => b.count - a.count);
    };

    const availableJobs = getAvailableJobs();

    // Apply all filters dynamically
    const filteredApplications = applications.filter(app => {
        // Search filter
        const matchesSearch = 
            app.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            app.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            app.position.toLowerCase().includes(searchTerm.toLowerCase()) ||
            app.phone.toLowerCase().includes(searchTerm.toLowerCase());
        
        if (!matchesSearch) return false;

        // Status filter
        const matchesStatus = statusFilter === 'all' || app.status === statusFilter;
        if (!matchesStatus) return false;

        // Job filter
        const matchesJob = jobFilter === 'all' || app.position === jobFilter;
        if (!matchesJob) return false;

        // Advanced filters
        // Qualification filter
        if (advancedFilters.qualifications.length > 0) {
            if (!app.qualification || !advancedFilters.qualifications.includes(app.qualification)) {
                return false;
            }
        }

        // Year of passing filter
        if (advancedFilters.yearOfPassingFrom || advancedFilters.yearOfPassingTo) {
            const yearOfPassing = parseInt(app.yearOfPassing || '0');
            const fromYear = parseInt(advancedFilters.yearOfPassingFrom || '0');
            const toYear = parseInt(advancedFilters.yearOfPassingTo || '9999');
            
            if (yearOfPassing < fromYear || yearOfPassing > toYear) {
                return false;
            }
        }

        // Experience level filter
        if (advancedFilters.experienceLevel.length > 0) {
            // More flexible matching - check for empty/null and case-insensitive comparison
            const appExpLevel = (app.experienceLevel || '').trim();
            const appTotalExp = (app.totalExperience || app.experience || '').toLowerCase();
            
            // If app has explicit experienceLevel, use it
            if (appExpLevel) {
                const matchesLevel = advancedFilters.experienceLevel.some(level => 
                    appExpLevel.toLowerCase() === level.toLowerCase()
                );
                if (!matchesLevel) return false;
            } else {
                // Fallback: Try to infer from totalExperience or experience field
                const isFresher = advancedFilters.experienceLevel.some(l => l.toLowerCase() === 'fresher');
                const isExperienced = advancedFilters.experienceLevel.some(l => l.toLowerCase() === 'experienced');
                
                // Parse experience to determine level
                const expMatch = appTotalExp.match(/(\d+)/);
                const yearsExp = expMatch ? parseInt(expMatch[1]) : 0;
                
                // Consider 0 years or "fresher" in text as Fresher
                const isFresherCandidate = yearsExp === 0 || 
                    appTotalExp.includes('fresher') || 
                    appTotalExp.includes('0 year') ||
                    appTotalExp === '' ||
                    appTotalExp === '0';
                    
                const isExperiencedCandidate = yearsExp > 0 && !appTotalExp.includes('fresher');
                
                // Check if matches selected filters
                let matches = false;
                if (isFresher && isFresherCandidate) matches = true;
                if (isExperienced && isExperiencedCandidate) matches = true;
                
                if (!matches) return false;
            }
        }

        // Years of experience filter (only if Experienced is selected)
        if (advancedFilters.experienceLevel.includes('Experienced') && 
            (advancedFilters.experienceYearsFrom || advancedFilters.experienceYearsTo)) {
            const expMatch = app.totalExperience?.match(/(\d+)/);
            const yearsExp = expMatch ? parseInt(expMatch[1]) : 0;
            const fromYears = parseInt(advancedFilters.experienceYearsFrom || '0');
            const toYears = parseInt(advancedFilters.experienceYearsTo || '999');
            
            if (yearsExp < fromYears || yearsExp > toYears) {
                return false;
            }
        }

        // Notice period filter
        if (advancedFilters.noticePeriods.length > 0) {
            if (!app.noticePeriod || !advancedFilters.noticePeriods.includes(app.noticePeriod)) {
                return false;
            }
        }

        // Skills filter (applicant must have ALL selected skills)
        if (advancedFilters.skills.length > 0) {
            if (!app.skills || !Array.isArray(app.skills)) {
                return false;
            }
            const hasAllSkills = advancedFilters.skills.every(skill => 
                app.skills!.some(appSkill => appSkill.toLowerCase() === skill.toLowerCase())
            );
            if (!hasAllSkills) {
                return false;
            }
        }
        
        return true;
    });

    const totalPages = Math.ceil(filteredApplications.length / itemsPerPage);
    const paginatedApplications = filteredApplications.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const statusColors = {
        new: 'bg-primary/20 text-primary border-primary/30',
        reviewing: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
        shortlisted: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
        interviewed: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
        rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
        hired: 'bg-green-500/20 text-green-400 border-green-500/30'
    };

    const visibilityColors = {
        visible: '',
        hidden: 'bg-red-500/20 text-red-400 border-red-500/30'
    };

    const handleRestore = async (id: string) => {
        try {
            await applicationsAPI.update(id, { visibility: 'visible' });
            setApplications(applications.map(app =>
                app.id === id ? { ...app, visibility: 'visible' } : app
            ));
            if (selectedApplication?.id === id) {
                setSelectedApplication({ ...selectedApplication, visibility: 'visible' });
            }
            toast.success('Application restored successfully');
        } catch (error) {
            console.error('Error restoring application:', error);
            toast.error('Failed to restore application');
        }
    };

    return (
        <AdminLayout>
            <AdminTopBar
                title="Job Applications"
                description="Review and manage job applications"
                actionButton={{
                    label: 'Export',
                    icon: <Download className="w-4 h-4 mr-2" />,
                    onClick: handleExport
                }}
            />

            <div className="p-8 space-y-6">
                {/* Search and Filter Bar */}
                <div className="glass-strong rounded-xl border border-primary/30 p-4">
                    <div className="flex flex-col gap-4">
                        <div className="flex flex-col md:flex-row gap-4">
                            {/* Search */}
                            <div className="flex-1 relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <input
                                    type="text"
                                    placeholder="Search by name, email, phone, or position..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 rounded-lg glass border border-border/30 text-foreground bg-background focus:outline-none focus:border-primary/50"
                                />
                            </div>

                            {/* Job Filter */}
                            <div className="relative">
                                <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                                <select
                                    value={jobFilter}
                                    onChange={(e) => {
                                        setJobFilter(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                    className="pl-10 pr-8 py-2 rounded-lg glass border border-border/30 text-foreground bg-background appearance-none cursor-pointer min-w-[180px]"
                                >
                                    <option value="all">
                                        {isLoading ? 'Loading...' : `All Jobs (${applications.length})`}
                                    </option>
                                    {availableJobs.map(job => (
                                        <option key={job.position} value={job.position}>
                                            {job.position} ({job.count})
                                        </option>
                                    ))}
                                </select>
                                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                            </div>

                            {/* Status Filter */}
                            <div className="relative">
                                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                                <select
                                    value={statusFilter}
                                    onChange={(e) => {
                                        setStatusFilter(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                    className="pl-10 pr-8 py-2 rounded-lg glass border border-border/30 text-foreground bg-background appearance-none cursor-pointer min-w-[150px]"
                                >
                                    <option value="all">All Status</option>
                                    <option value="new">New</option>
                                    <option value="reviewing">Reviewing</option>
                                    <option value="shortlisted">Shortlisted</option>
                                    <option value="interviewed">Interviewed</option>
                                    <option value="rejected">Rejected</option>
                                    <option value="hired">Hired</option>
                                </select>
                                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                            </div>

                            {/* Advanced Filter Button */}
                            <Button
                                onClick={() => setIsAdvancedFilterOpen(true)}
                                variant="outline"
                                className={`border-border/30 whitespace-nowrap ${
                                    getActiveFilterCount() > 0 ? 'border-primary/50 text-primary' : ''
                                }`}
                            >
                                <SlidersHorizontal className="w-4 h-4 mr-2" />
                                Advanced
                                {getActiveFilterCount() > 0 && (
                                    <span className="ml-2 px-2 py-0.5 rounded-full bg-primary/20 text-primary text-xs">
                                        {getActiveFilterCount()}
                                    </span>
                                )}
                            </Button>
                        </div>

                        {/* Active Filters Summary */}
                        {getActiveFilterCount() > 0 && (
                            <div className="flex items-center gap-2 text-sm">
                                <span className="text-muted-foreground">Filters:</span>
                                <div className="flex flex-wrap gap-2">
                                    {jobFilter !== 'all' && (
                                        <Badge className="bg-primary/20 text-primary border-primary/30 text-xs">
                                            Job: {jobFilter}
                                        </Badge>
                                    )}
                                    {advancedFilters.qualifications.length > 0 && (
                                        <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 text-xs">
                                            {advancedFilters.qualifications.length} Qualification{advancedFilters.qualifications.length > 1 ? 's' : ''}
                                        </Badge>
                                    )}
                                    {advancedFilters.experienceLevel.length > 0 && (
                                        <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 text-xs">
                                            {advancedFilters.experienceLevel.join(', ')}
                                        </Badge>
                                    )}
                                    {advancedFilters.skills.length > 0 && (
                                        <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">
                                            {advancedFilters.skills.length} Skill{advancedFilters.skills.length > 1 ? 's' : ''}
                                        </Badge>
                                    )}
                                    {(advancedFilters.yearOfPassingFrom || advancedFilters.yearOfPassingTo) && (
                                        <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 text-xs">
                                            Year: {advancedFilters.yearOfPassingFrom || '...'}-{advancedFilters.yearOfPassingTo || '...'}
                                        </Badge>
                                    )}
                                    {advancedFilters.noticePeriods.length > 0 && (
                                        <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/30 text-xs">
                                            {advancedFilters.noticePeriods.length} Notice Period{advancedFilters.noticePeriods.length > 1 ? 's' : ''}
                                        </Badge>
                                    )}
                                </div>
                                <button
                                    onClick={() => {
                                        setJobFilter('all');
                                        setAdvancedFilters({
                                            qualifications: [],
                                            yearOfPassingFrom: '',
                                            yearOfPassingTo: '',
                                            experienceLevel: [],
                                            experienceYearsFrom: '',
                                            experienceYearsTo: '',
                                            noticePeriods: [],
                                            skills: []
                                        });
                                    }}
                                    className="text-xs text-primary hover:text-primary/80 underline ml-auto"
                                >
                                    Clear all
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-7 gap-3">
                    <div className="glass-strong rounded-xl border border-primary/30 p-3">
                        <p className="text-xs text-muted-foreground mb-1">Total Applications</p>
                        {isLoading ? (
                            <div className="h-6 w-12 bg-muted/20 animate-pulse rounded"></div>
                        ) : (
                            <p className="text-xl text-foreground">{applications.length}</p>
                        )}
                    </div>
                    <div className="glass-strong rounded-xl border border-blue-500/30 p-3">
                        <p className="text-xs text-muted-foreground mb-1">New</p>
                        {isLoading ? (
                            <div className="h-6 w-8 bg-muted/20 animate-pulse rounded"></div>
                        ) : (
                            <p className="text-xl text-blue-400">{applications.filter(a => a.status === 'new').length}</p>
                        )}
                    </div>
                    <div className="glass-strong rounded-xl border border-purple-500/30 p-3">
                        <p className="text-xs text-muted-foreground mb-1">Reviewing</p>
                        {isLoading ? (
                            <div className="h-6 w-8 bg-muted/20 animate-pulse rounded"></div>
                        ) : (
                            <p className="text-xl text-purple-400">{applications.filter(a => a.status === 'reviewing').length}</p>
                        )}
                    </div>
                    <div className="glass-strong rounded-xl border border-yellow-500/30 p-3">
                        <p className="text-xs text-muted-foreground mb-1">Shortlisted</p>
                        {isLoading ? (
                            <div className="h-6 w-8 bg-muted/20 animate-pulse rounded"></div>
                        ) : (
                            <p className="text-xl text-yellow-400">{applications.filter(a => a.status === 'shortlisted').length}</p>
                        )}
                    </div>
                    <div className="glass-strong rounded-xl border border-cyan-500/30 p-3">
                        <p className="text-xs text-muted-foreground mb-1">Interviewed</p>
                        {isLoading ? (
                            <div className="h-6 w-8 bg-muted/20 animate-pulse rounded"></div>
                        ) : (
                            <p className="text-xl text-cyan-400">{applications.filter(a => a.status === 'interviewed').length}</p>
                        )}
                    </div>
                    <div className="glass-strong rounded-xl border border-red-500/30 p-3">
                        <p className="text-xs text-muted-foreground mb-1">Rejected</p>
                        {isLoading ? (
                            <div className="h-6 w-8 bg-muted/20 animate-pulse rounded"></div>
                        ) : (
                            <p className="text-xl text-red-400">{applications.filter(a => a.status === 'rejected').length}</p>
                        )}
                    </div>
                    <div className="glass-strong rounded-xl border border-green-500/30 p-3">
                        <p className="text-xs text-muted-foreground mb-1">Hired</p>
                        {isLoading ? (
                            <div className="h-6 w-8 bg-muted/20 animate-pulse rounded"></div>
                        ) : (
                            <p className="text-xl text-green-400">{applications.filter(a => a.status === 'hired').length}</p>
                        )}
                    </div>
                </div>

                {/* Applications Table */}
                <div className="glass-strong rounded-xl border border-primary/30 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-border/30 bg-primary/5">
                                    <th className="text-left p-4 text-foreground">Name</th>
                                    <th className="text-left p-4 text-foreground">Email</th>
                                    <th className="text-left p-4 text-foreground">Phone</th>
                                    <th className="text-left p-4 text-foreground">Role</th>
                                    <th className="text-left p-4 text-foreground">Applied Date</th>
                                    <th className="text-left p-4 text-foreground">Status</th>
                                    <th className="text-left p-4 text-foreground">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    // Loading skeleton rows
                                    Array.from({ length: 5 }).map((_, index) => (
                                        <tr key={index} className="border-b border-border/30">
                                            <td className="p-4">
                                                <div className="h-4 w-24 bg-muted/20 animate-pulse rounded"></div>
                                            </td>
                                            <td className="p-4">
                                                <div className="h-4 w-32 bg-muted/20 animate-pulse rounded"></div>
                                            </td>
                                            <td className="p-4">
                                                <div className="h-4 w-20 bg-muted/20 animate-pulse rounded"></div>
                                            </td>
                                            <td className="p-4">
                                                <div className="h-4 w-28 bg-muted/20 animate-pulse rounded"></div>
                                            </td>
                                            <td className="p-4">
                                                <div className="h-4 w-16 bg-muted/20 animate-pulse rounded"></div>
                                            </td>
                                            <td className="p-4">
                                                <div className="h-4 w-20 bg-muted/20 animate-pulse rounded"></div>
                                            </td>
                                            <td className="p-4">
                                                <div className="flex gap-2">
                                                    <div className="h-8 w-8 bg-muted/20 animate-pulse rounded"></div>
                                                    <div className="h-8 w-8 bg-muted/20 animate-pulse rounded"></div>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : paginatedApplications.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className="p-8">
                                            <EmptyState
                                                icon={Briefcase}
                                                title="No applications found"
                                                description="Try adjusting your search or filters"
                                            />
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedApplications.map((app, index) => (
                                        <motion.tr
                                            key={app.id}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: index * 0.05 }}
                                            className="border-b border-border/30 hover:bg-primary/5 transition-colors cursor-pointer"
                                            onClick={() => setSelectedApplication(app)}
                                        >
                                            <td className="p-4">
                                                <div>
                                                    <p className="text-foreground">{app.name}</p>
                                                    {app.rating && (
                                                        <div className="flex items-center gap-1 mt-1">
                                                            <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                                                            <span className="text-xs text-muted-foreground">{app.rating}/5</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="p-4">
                                                <a
                                                    href={`mailto:${app.email}`}
                                                    className="text-primary hover:text-primary/80 text-sm"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    {app.email}
                                                </a>
                                            </td>
                                            <td className="p-4">
                                                <p className="text-foreground text-sm">{app.phone}</p>
                                            </td>
                                            <td className="p-4">
                                                <p className="text-foreground text-sm">{app.position}</p>
                                            </td>
                                            <td className="p-4">
                                                <p className="text-muted-foreground text-sm">
                                                    {new Date(app.appliedDate).toLocaleDateString('en-US', {
                                                        year: 'numeric',
                                                        month: 'short',
                                                        day: 'numeric'
                                                    })}
                                                </p>
                                            </td>
                                            <td className="p-4">
                                                <div className="flex gap-1 flex-wrap">
                                                    <Badge className={`text-xs ${statusColors[app.status]}`}>
                                                        {app.status}
                                                    </Badge>
                                                    {app.visibility === 'hidden' && (
                                                        <Badge className={`text-xs ${visibilityColors.hidden}`}>
                                                            Deleted
                                                        </Badge>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="p-4">
                                                <div className="flex items-center gap-2">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSelectedApplication(app);
                                                        }}
                                                        className="text-primary hover:text-primary/80"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </Button>
                                                    {isSuperAdmin && app.visibility === 'hidden' && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleRestore(app.id);
                                                            }}
                                                            className="text-green-400 hover:text-green-300"
                                                            title="Restore Application"
                                                        >
                                                            <RotateCcw className="w-4 h-4" />
                                                        </Button>
                                                    )}
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDelete(app.id);
                                                        }}
                                                        className="text-red-400 hover:text-red-300"
                                                        title={isSuperAdmin ? "Permanent Delete" : "Delete"}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </td>
                                        </motion.tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {filteredApplications.length > 0 && (
                        <div className="p-4 border-t border-border/30">
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={setCurrentPage}
                                itemsPerPage={itemsPerPage}
                                totalItems={filteredApplications.length}
                            />
                        </div>
                    )}
                </div>

                <BackLink to="/admin" label="Back to Dashboard" />
            </div>

            {/* Application Detail Modal */}
            <AnimatePresence>
                {selectedApplication && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                        onClick={() => setSelectedApplication(null)}
                    >
                        <motion.div
                            initial={{ scale: 0.9, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.9, y: 20 }}
                            onClick={(e) => e.stopPropagation()}
                            className="glass-strong rounded-2xl border border-primary/30 p-6 w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
                        >
                            <div className="flex items-start justify-between mb-6">
                                <div>
                                    <h2 className="text-foreground text-2xl mb-2">{selectedApplication.name}</h2>
                                    <div className="flex gap-2 items-center flex-wrap">
                                        <Badge className={`text-xs ${statusColors[selectedApplication.status]}`}>
                                            {selectedApplication.status}
                                        </Badge>
                                        {selectedApplication.visibility === 'hidden' && (
                                            <Badge className={`text-xs ${visibilityColors.hidden}`}>
                                                Deleted
                                            </Badge>
                                        )}
                                        {selectedApplication.rating && (
                                            <div className="flex items-center gap-1 px-2 py-1 rounded-full glass border border-border/30">
                                                <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                                                <span className="text-xs text-foreground">{selectedApplication.rating}/5</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => setSelectedApplication(null)}
                                >
                                    <X className="w-5 h-5" />
                                </Button>
                            </div>

                            <div className="overflow-y-auto flex-1 space-y-6">
                                {/* Contact Information */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-3 p-3 rounded-lg glass">
                                            <Briefcase className="w-4 h-4 text-muted-foreground" />
                                            <div>
                                                <p className="text-xs text-muted-foreground">Position</p>
                                                <p className="text-foreground">{selectedApplication.position}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3 p-3 rounded-lg glass">
                                            <Mail className="w-4 h-4 text-muted-foreground" />
                                            <div>
                                                <p className="text-xs text-muted-foreground">Email</p>
                                                <a
                                                    href={`mailto:${selectedApplication.email}`}
                                                    className="text-primary hover:text-primary/80 text-sm"
                                                >
                                                    {selectedApplication.email}
                                                </a>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3 p-3 rounded-lg glass">
                                            <Phone className="w-4 h-4 text-muted-foreground" />
                                            <div>
                                                <p className="text-xs text-muted-foreground">Phone</p>
                                                <p className="text-foreground">{selectedApplication.phone}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-3 p-3 rounded-lg glass">
                                            <MapPin className="w-4 h-4 text-muted-foreground" />
                                            <div>
                                                <p className="text-xs text-muted-foreground">Location</p>
                                                <p className="text-foreground">{selectedApplication.location}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3 p-3 rounded-lg glass">
                                            <Calendar className="w-4 h-4 text-muted-foreground" />
                                            <div>
                                                <p className="text-xs text-muted-foreground">Applied Date</p>
                                                <p className="text-foreground">
                                                    {new Date(selectedApplication.appliedDate).toLocaleDateString('en-US', {
                                                        year: 'numeric',
                                                        month: 'long',
                                                        day: 'numeric'
                                                    })}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Experience & Education */}
                                <div className="space-y-4">
                                    <div className="p-4 rounded-lg glass border border-border/30">
                                        <h4 className="text-foreground mb-2">Experience</h4>
                                        <p className="text-muted-foreground text-sm">{selectedApplication.experience}</p>
                                    </div>
                                    <div className="p-4 rounded-lg glass border border-border/30">
                                        <h4 className="text-foreground mb-2">Education</h4>
                                        <p className="text-muted-foreground text-sm">{selectedApplication.education}</p>
                                    </div>
                                </div>

                                {/* Skills */}
                                <div className="p-4 rounded-lg glass border border-border/30">
                                    <h4 className="text-foreground mb-3">Skills</h4>
                                    <div className="flex flex-wrap gap-2">
                                        {selectedApplication.skills.map((skill, index) => (
                                            <Badge key={index} className="bg-primary/20 text-primary border-primary/30">
                                                {skill}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>

                                {/* Cover Letter */}
                                <div className="p-4 rounded-lg glass border border-border/30">
                                    <h4 className="text-foreground mb-2">Cover Letter</h4>
                                    <p className="text-muted-foreground text-sm whitespace-pre-wrap">{selectedApplication.coverLetter}</p>
                                </div>

                                {/* Additional Details */}
                                {(selectedApplication.currentCtc || selectedApplication.expectedSalary || selectedApplication.noticePeriod) && (
                                    <div className="p-4 rounded-lg glass border border-border/30">
                                        <h4 className="text-foreground mb-3">Compensation & Availability</h4>
                                        <div className="space-y-2 text-sm">
                                            {selectedApplication.currentCtc && (
                                                <div className="flex justify-between">
                                                    <span className="text-muted-foreground">Current CTC:</span>
                                                    <span className="text-foreground">{selectedApplication.currentCtc}</span>
                                                </div>
                                            )}
                                            {selectedApplication.expectedSalary && (
                                                <div className="flex justify-between">
                                                    <span className="text-muted-foreground">Expected Salary:</span>
                                                    <span className="text-foreground">{selectedApplication.expectedSalary}</span>
                                                </div>
                                            )}
                                            {selectedApplication.noticePeriod && (
                                                <div className="flex justify-between">
                                                    <span className="text-muted-foreground">Notice Period:</span>
                                                    <span className="text-foreground">{selectedApplication.noticePeriod}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Personal Information */}
                                {(selectedApplication.dob || selectedApplication.gender || selectedApplication.linkedinUrl || selectedApplication.portfolioUrl || selectedApplication.currentAddress) && (
                                    <div className="p-4 rounded-lg glass border border-border/30">
                                        <h4 className="text-foreground mb-3">Personal Information</h4>
                                        <div className="space-y-2 text-sm">
                                            {selectedApplication.dob && (
                                                <div className="flex justify-between">
                                                    <span className="text-muted-foreground">Date of Birth:</span>
                                                    <span className="text-foreground">{new Date(selectedApplication.dob).toLocaleDateString()}</span>
                                                </div>
                                            )}
                                            {selectedApplication.gender && (
                                                <div className="flex justify-between">
                                                    <span className="text-muted-foreground">Gender:</span>
                                                    <span className="text-foreground">{selectedApplication.gender}</span>
                                                </div>
                                            )}
                                            {selectedApplication.linkedinUrl && (
                                                <div className="flex justify-between">
                                                    <span className="text-muted-foreground">LinkedIn:</span>
                                                    <a 
                                                        href={selectedApplication.linkedinUrl} 
                                                        target="_blank" 
                                                        rel="noopener noreferrer"
                                                        className="text-primary hover:text-primary/80"
                                                    >
                                                        View Profile
                                                    </a>
                                                </div>
                                            )}
                                            {selectedApplication.portfolioUrl && (
                                                <div className="flex justify-between">
                                                    <span className="text-muted-foreground">Portfolio:</span>
                                                    <a 
                                                        href={selectedApplication.portfolioUrl} 
                                                        target="_blank" 
                                                        rel="noopener noreferrer"
                                                        className="text-primary hover:text-primary/80"
                                                    >
                                                        View Portfolio
                                                    </a>
                                                </div>
                                            )}
                                            {selectedApplication.currentAddress && (
                                                <div className="flex justify-between">
                                                    <span className="text-muted-foreground">Address:</span>
                                                    <span className="text-foreground">{selectedApplication.currentAddress}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Resume File */}
                                {selectedApplication.resumeFileName && (
                                    <div className="p-4 rounded-lg glass border border-border/30">
                                        <div className="flex justify-between items-center">
                                            <div>
                                                <p className="text-xs text-muted-foreground mb-1">Resume File</p>
                                                <p className="text-foreground text-sm">{selectedApplication.resumeFileName}</p>
                                            </div>
                                            <Button
                                                onClick={() => downloadBase64File(selectedApplication.resumeUrl, selectedApplication.resumeFileName || 'resume.pdf')}
                                                variant="outline"
                                                size="sm"
                                                className="glass border-border/30"
                                            >
                                                <ExternalLink className="w-4 h-4 mr-2" />
                                                View
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Actions */}
                            <div className="flex gap-3 pt-6 border-t border-border/30 mt-6">
                                <select
                                    value={selectedApplication.status}
                                    onChange={(e) => handleStatusChange(selectedApplication.id, e.target.value as StoredApplication['status'])}
                                    className="flex-1 px-4 py-2 rounded-lg glass border border-border/30 text-foreground bg-background"
                                >
                                    <option value="new">New</option>
                                    <option value="reviewing">Reviewing</option>
                                    <option value="shortlisted">Shortlisted</option>
                                    <option value="interviewed">Interviewed</option>
                                    <option value="rejected">Rejected</option>
                                    <option value="hired">Hired</option>
                                </select>
                                <Button
                                    onClick={() => handleDelete(selectedApplication.id)}
                                    variant="outline"
                                    className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                                >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete
                                </Button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Delete Confirmation Modal */}
            <DeleteConfirmationModal
                isOpen={deleteModal.isOpen}
                onClose={() => !deleteModal.isDeleting && setDeleteModal({ isOpen: false, itemId: '', itemName: '', isDeleting: false })}
                onConfirm={confirmDelete}
                title={isSuperAdmin ? "Permanently Delete Application" : "Delete Application"}
                message={isSuperAdmin ? "Are you sure you want to permanently delete this application?" : "Are you sure you want to delete this application?"}
                itemName={deleteModal.itemName}
                isDeleting={deleteModal.isDeleting}
                isPermanent={isSuperAdmin}
            />

            {/* Advanced Filter Modal */}
            <AdvancedFilterModal
                isOpen={isAdvancedFilterOpen}
                onClose={() => setIsAdvancedFilterOpen(false)}
                applications={filteredApplications}
                currentFilters={advancedFilters}
                onApplyFilters={handleApplyAdvancedFilters}
            />

            {/* Filter Debug Panel (Development Tool) */}
            <FilterDebugPanel
                applications={applications}
                filteredApplications={filteredApplications}
            />
        </AdminLayout>
    );
}
